import React, { useState, useEffect, useRef, useMemo } from "react";
import cryptoRandomString from "crypto-random-string";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { xml2js } from "../../json/xml2js";
import queryString from "query-string";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import moment from "moment";
import ClientFormHeader from "../common/ClientFormHeader";
import FormButtonBar from "../common/FormButtonBar";
import Signature from "../common/Signature";
import Comment from "../common/Comment";
import BackIcon from "../common/BackIcon";
import ShareModal from "../common/ShareModal";
import {
  FormInput,
  Select,
  FormCheckbox,
  FormTextArea,
  PhoneInput,
} from "../common/ControlBundle";
import {
  getCareTeams,
  getConfig,
  getSubmission,
  saveForm,
  cloneForm,
  deleteFormSubmission,
  validateForm,
  handleFormChange,
  compareForms,
  getFormsySubmissionsForClient,
  sendShareEmail,
  getClientData,
  handleCopyUrl,
} from "../../scripts/formScripts";
import {
  getFieldName,
  getFieldValue,
  getFieldDate,
  ArrayCheck,
} from "../../scripts/helperScripts";
import { getSchema } from "./joi/validation";

function SAFYAdultSafetyPlanv2() {
  const location = useLocation();
  const navigate = useNavigate();
  const initialProgram = location.state?.selectedProgram;

  //Get route and query string params
  const { clientid, formid, submissionid } = useParams();
  const qs = queryString.parse(location.search);
  console.log("Params", clientid, formid, submissionid);
  console.log("QS:", qs);

  //Get logged in user info
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));
  //The complete Form Submission object with form data
  const [formsyDetails, setFormsyDetails] = useState({});
  const [formsyConfig, setFormsyConfig] = useState({});
  const [loadingConfig, setLoadingConfig] = useState(false);
  //The form data and previous forms
  const [data, setData] = useState({});
  const [allFormSubmissions, setAllFormSubmissions] = useState([]);
  //Touched fields
  const [touched, setTouched] = useState({});
  const [isSaveInProgress, setIsSaveInProgress] = useState(false);
  //Errors
  const [errors, setErrors] = useState({});
  //Client, Program and CareTeam Info
  const [clientData, setClientData] = useState({});
  const [careTeams, setCareTeams] = useState([]);

  //Signatures State Property
  const [signatures, setSignatures] = useState([]);
  //Show Signature Modal
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  //Comments state Property
  const [comments, setComments] = useState();
  //Show Comments Modal
  const [showComment, setShowComment] = useState(false);
  //Show Share Modal
  const [showShare, setShowShare] = useState(false);
  //Show Form Level (not field specific) Comments
  const [showFormComment, setShowFormComment] = useState(false);

  const [doCompare, setDoCompare] = useState(false);
  const [timeoutId, setTimeoutId] = useState();

  const pdfRef = useRef(true);
  const isMountedRef = useRef(true);

  //Get validation schema
  const schema = getSchema(formid);
  console.log("Schema Config: ", schema);

  const selectedProgramId = useMemo(() => {
    return data.SelectedProgram?.["@value"];
  }, [data.SelectedProgram]);

  const formDisabled = useMemo(() => {
    return formsyDetails.Active === "0" || formsyDetails.FinalSigned === "1";
  }, [formsyDetails]);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      pdfRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (
      !initialProgram?.ccx_client_programid ||
      !data.SelectedProgram ||
      data.SelectedProgram["@value"]
    ) {
      return;
    }

    const obj = { ...data };
    obj.SelectedProgram["@value"] = initialProgram.ccx_client_programid;
    setData(obj);

    if (formsyDetails.ClientProgramId === initialProgram.ccx_client_programid) {
      return;
    }

    const details = { ...formsyDetails };
    details.ClientProgramId = initialProgram.ccx_client_programid;
    details.ClientProgramName = getFieldName(initialProgram.ccx_programunit);
    setFormsyDetails(details);
  }, [initialProgram, selectedProgramId, data, formsyDetails]);

  //Get Client Info
  useEffect(() => {
    if (!formid || !clientid) {
      return;
    }

    const loadClientInfo = async () => {
      const response = await Promise.all([
        getFormsySubmissionsForClient(formid, clientid),
        getCareTeams(clientid),
      ]);

      if (!isMountedRef.current || !response) {
        return;
      }

      setAllFormSubmissions(response[0]);
      setCareTeams(response[1]);
    };

    loadClientInfo();

    if (submissionid !== undefined) {
      const loadSubmission = async () => {
        const response = await getSubmission(submissionid);

        if (!isMountedRef.current || !response) {
          return;
        }

        setData(response.Data);
        setFormsyDetails(response.FormsyDetails);

        if (response.Comments) {
          setComments(response.Comments);
        }

        if (response.Signatures) {
          setSignatures(response.Signatures);
        }
      };

      loadSubmission();
    }
  }, [formid, clientid, submissionid]);

  useEffect(() => {
    if (
      !formid ||
      !clientid ||
      Object.keys(formsyConfig).length > 0 ||
      loadingConfig ||
      !safyInfo?.user
    ) {
      return;
    }

    const loadConfig = async () => {
      setLoadingConfig(true);
      const response = await getConfig(formid);

      if (!isMountedRef.current || !response) {
        return;
      }

      setFormsyConfig(response);
      const clientDataResponse = await getClientData(
        clientid,
        submissionid !== undefined,
        `${response.FormName}${
          response.VersionIntId > 1 ? `v${response.VersionIntId}` : ""
        }`
      );

      if (!isMountedRef.current || !clientDataResponse) {
        return;
      }

      setClientData(clientDataResponse);

      if (!submissionid) {
        //Set Formsy Details
        const newForm = {
          SubmissionId: "",
          FormId: response.FormId,
          FormVersionId: formid,
          ContactId: clientid,
          ContactName: clientDataResponse.client.fullName,
          ClientProgramId: "",
          ClientProgramName: "",
          TertiaryObjectId: "",
          TertiaryObjectName: "",
          FormResults: {
            results: {},
          },

          Comments: "",
          Signatures: "",
          CreatedOn: "",
          CreatedById: safyInfo.user.azureId,
          CreatedByName: safyInfo.user.displayName,
          ModifiedById: safyInfo.user.azureId,
          ModifiedByName: safyInfo.user.displayName,
          FinalSigned: "0",
          FinalSignedDate: "",
          Active: "1",
        };
        setFormsyDetails(newForm);
        setData(response.FormConfiguration[0].config[0]);
        setLoadingConfig(false);

        console.log("New Form:", newForm);
        console.log(
          "loadConfig setData:",
          response.FormConfiguration[0].config[0]
        );
      }
    };

    loadConfig();
  }, [
    loadingConfig,
    formid,
    clientid,
    safyInfo?.user,
    formsyConfig,
    submissionid,
  ]);

  useEffect(() => {
    if (!submissionid || !!formsyDetails.SubmissionId) {
      return;
    }

    const obj = { ...formsyDetails };
    obj.SubmissionId = submissionid;
    setFormsyDetails(obj);
  }, [submissionid, formsyDetails]);

  const helperConfiguration = useMemo(() => {
    try {
      return JSON.parse(formsyConfig.HelperConfiguration[0]);
    } catch {
      const defaultConfig = {
        buttonBarConfig: {
          sign: {
            display: false,
            initialSignatureType: "full",
          },
          clone: {
            display: false,
          },
          delete: {
            display: false,
          },
          share: {
            display: false,
          },
          comment: {
            display: false,
          },
        },
        signatureType: "full",
        finalSignType: "Provider",
      };

      return defaultConfig;
    }
  }, [formsyConfig.HelperConfiguration]);

  const getSelectedProgramName = (dataObj, cData) => {
    if (!dataObj.SelectedProgram || !cData.programs) {
      return "";
    }

    let name = getFieldName(dataObj.SelectedProgram);
    if (name) {
      return name;
    } else {
      const selectedProgram = cData.programs.filter(
        (p) => p.ccx_client_programid === getFieldValue(dataObj.SelectedProgram)
      )[0];

      if (selectedProgram) {
        name = getFieldName(selectedProgram.ccx_programunit);
        const programDates = ` (${getFieldDate(
          selectedProgram.ccx_admitdate
        )} - ${
          selectedProgram.ccx_dischargedate
            ? getFieldDate(selectedProgram.ccx_dischargedate)
            : "no date"
        })`;
        return name + programDates;
      } else {
        return "";
      }
    }
  };

  //Form validation
  const validate = () => {
    const results = validateForm(data, schema);
    setErrors(results);
    return results;
  };

  const handleChange = ({ currentTarget: input }) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const result = handleFormChange(input, data, schema, errors);
    setData(result.Data);
    setErrors(result.Errors);

    if (!submissionid) {
      return result.Data;
    }

    const touchedObj = { ...touched, [input.name]: true };
    const touchedFields = Object.keys(touchedObj).filter(
      (field) => touchedObj[field]
    );

    if (touchedFields.length >= 5) {
      if (isSaveInProgress) {
        const toId = setTimeout(() => {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }, 20000);

        setTimeoutId(toId);
      } else {
        clearTimeout(timeoutId);
        handleSubmit(result.Data, null, null, null, true);
        touchedFields.forEach((field) => (touchedObj[field] = false));
      }
    } else {
      const toId = setTimeout(() => {
        if (isSaveInProgress) {
          const toId2 = setTimeout(() => {
            touchedFields.forEach((field) => (touchedObj[field] = false));
            handleSubmit(result.Data, null, null, null, true);
          }, 20000);

          setTimeoutId(toId2);
        } else {
          touchedFields.forEach((field) => (touchedObj[field] = false));
          handleSubmit(result.Data, null, null, null, true);
        }
      }, 20000);

      setTimeoutId(toId);
    }

    setTouched(touchedObj);
    return result.Data;
  };

  ////SAVE
  const handleSubmit = async (
    dataOverride,
    formsyDetailsOverride,
    commentsOverride,
    signaturesOverride,
    autoSave = false
  ) => {
    if (isSaveInProgress) {
      return;
    }

    const dataObj = dataOverride || data;
    const formsyDetailsObj = formsyDetailsOverride || formsyDetails;
    const commentsObj =
      commentsOverride === undefined ? undefined : commentsOverride || comments;
    const signaturesObj = signaturesOverride || signatures;

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    setTouched({});
    const errs = { ...errors };

    if (!dataObj.SelectedProgram?.["@value"]) {
      errs.SelectedProgram = '"Selected Program" is required';
      setErrors(errs);
      return;
    }

    delete errs.SelectedProgram;
    setErrors(errs);
    setIsSaveInProgress(true);

    try {
      const submissionResponse = await saveForm(
        dataObj,
        formsyDetailsObj,
        commentsObj,
        signaturesObj,
        navigate,
        location,
        autoSave
      );

      if (!isMountedRef.current || !submissionResponse?.Id) {
        return;
      }

      const newDetails = { ...formsyDetailsObj };
      newDetails.ModifiedOn = submissionResponse.LastModifiedOn;
      newDetails.ModifiedById = safyInfo.user.azureId;
      newDetails.ModifiedByName = safyInfo.user.displayName;

      if (newDetails.SubmissionId === "") {
        newDetails.SubmissionId = submissionResponse?.Id;
      }

      setFormsyDetails(newDetails);
    } finally {
      setIsSaveInProgress(false);
    }
  };

  ////CLONE
  const handleOnClone = (event) => {
    event.preventDefault();
    cloneForm(
      data,
      formsyDetails,
      comments,
      signatures,
      navigate,
      location,
      formsyConfig
    );
    setSignatures([]);
  };

  ////DELETE
  const handleDelete = (e) => {
    e.preventDefault();
    if (submissionid) {
      deleteFormSubmission(
        data,
        formsyDetails,
        comments,
        signatures,
        navigate,
        location
      );
    } else {
      alert("The form could not be deleted because it has not been saved");
    }
  };

  ////SHARE
  const toggleShareModal = () => {
    setShowShare(!showShare);
  };

  const handleShareSend = (recipientList, shareComments) => {
    sendShareEmail(
      recipientList,
      shareComments,
      location.pathname,
      data,
      formsyDetails,
      comments,
      signatures,
      formsyConfig.FormDescription
    );
  };

  ////COMMENTS FUNCTIONALITY
  const [commentToEdit, setCommentToEdit] = useState();

  const toggleCommentModal = (editComment) => {
    if (!showComment && editComment) setCommentToEdit(editComment);
    setShowComment(!showComment);
  };

  const handleComments = ({ action, fieldName, comment, acknowledge }) => {
    if (action === "ADD") {
      setShowComment(true);
    } else if (comment !== undefined) {
      if (action === "EDIT") {
        toggleCommentModal(comment);
      } else if (action === "DELETE") {
        handleDeleteComment(comment);
      } else if (action === "ACKNOWLEDGE" && acknowledge !== undefined) {
        handleAcknowledgeComment(comment, acknowledge);
      }
    } else if (action === "GET") {
      return parsedComments.filter(
        (comment) => comment.field._text === fieldName
      );
    }
  };

  const commentFields = () => {
    let commentOptions = [];
    commentOptions.push({ _id: "EntireForm", name: "Entire Form----" });
    for (const property in data) {
      if (
        data[property]["@type"] !== "section" &&
        data[property]["@type"] !== "label" &&
        data[property]["@type"] !== "User" &&
        data[property]["@type"] !== "hidden"
      ) {
        const option = {
          _id: property,
          name:
            data[property]["@label"] !== undefined &&
            data[property]["@label"] !== ""
              ? data[property]["@label"]
              : property,
        };
        commentOptions.push(option);
      }
    }
    return commentOptions;
  };

  const parsedComments = useMemo(() => {
    if (!comments) {
      return [];
    }

    const commentObj = xml2js(`<comments type="array">${comments}</comments>`, {
      compact: true,
      spaces: 4,
    });

    return ArrayCheck(commentObj.comments.comment);
  }, [comments]);

  const onCommentSave = (comment, commentToEdit) => {
    let formsyComments;
    if (commentToEdit) {
      const commentXml = `<comment><createdby>${
        commentToEdit.createdby._text
      }</createdby><datetime>${commentToEdit.datetime._text}</datetime><field>${
        commentToEdit.field._text
      }</field><comment>${commentToEdit.comment._text}</comment>${
        commentToEdit.acknowledgedby
          ? `<acknowledgedby>${commentToEdit.acknowledgedby._text}</acknowledgedby><acknowledgedon>${commentToEdit.acknowledgedon._text}</acknowledgedon>`
          : ""
      }</comment>`;
      console.log("CurrentCommentsXML: ", comments);
      console.log("CommentToReplace: ", commentXml);
      console.log("CommentToEdit: ", commentToEdit);
      console.log("CommentToEditXML: ", comment);

      formsyComments =
        comments !== undefined
          ? comments.replace(commentXml, comment)
          : comment;
      setComments(formsyComments);
    } else {
      formsyComments = comments !== undefined ? comments + comment : comment;
      setComments(formsyComments);
    }

    setShowComment(false);
    handleSubmit(null, null, formsyComments, null);
    setCommentToEdit(undefined);
  };

  const handleDeleteComment = (comment) => {
    const commentXml = `<comment><createdby>${
      safyInfo.user.displayName
    }</createdby><datetime>${comment.datetime._text}</datetime><field>${
      comment.field._text
    }</field><comment>${comment.comment._text}</comment>${
      comment.acknowledgedby
        ? `<acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon>`
        : ""
    }</comment>`;

    let formsyComments = comments.replace(commentXml, "");
    if (formsyComments === "") formsyComments = undefined;
    setComments(formsyComments);
    handleSubmit(null, null, formsyComments, null);
  };

  const toggleCommentToEdit = (comment) => {
    setCommentToEdit(comment);
    setShowComment(true);
  };

  const handleAcknowledgeComment = (comment, acknowledge) => {
    if (acknowledge) {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const newCommentXML = `<comment><createdby>${
        comment.createdby._text
      }</createdby><datetime>${comment.datetime._text}</datetime><field>${
        comment.field._text
      }</field><comment>${comment.comment._text}</comment><acknowledgedby>${
        safyInfo.user.displayName
      }</acknowledgedby><acknowledgedon>${moment().format(
        "L"
      )} ${moment().format("LT")}</acknowledgedon></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    } else {
      const oldCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment><acknowledgedby>${comment.acknowledgedby._text}</acknowledgedby><acknowledgedon>${comment.acknowledgedon._text}</acknowledgedon></comment>`;
      const newCommentXML = `<comment><createdby>${comment.createdby._text}</createdby><datetime>${comment.datetime._text}</datetime><field>${comment.field._text}</field><comment>${comment.comment._text}</comment></comment>`;
      const formsyComments = comments.replace(oldCommentXML, newCommentXML);
      setComments(formsyComments);
      handleSubmit(null, null, formsyComments, null);
    }
  };

  const handleCommentNavigation = (fieldName) => {
    const element = document.getElementById(fieldName);
    if (element.type === "checkbox")
      scrollTo(
        document.getElementById(fieldName).parentElement.parentElement
          .parentElement.offsetTop - 50,
        () => {
          document.getElementById(fieldName).focus();
        }
      );
    else
      scrollTo(document.getElementById(fieldName).offsetTop - 50, () => {
        document.getElementById(fieldName).focus();
      });
  };

  const renderComments = () => {
    return parsedComments
      .filter(
        (comment) =>
          comment.field._text === "EntireForm" ||
          comment.field._text === "Entire Form----"
      )
      .map((comment, index) => (
        <div className="mt-2" key={`form-comment-${index}`}>
          {showFormComment && (
            <div
              className={`${
                comment.acknowledgedby ? "bg-safy3-50" : "bg-warning"
              } p-2 m-2 rounded d-print-none`}
              title={
                comment.acknowledgedby
                  ? `Acknowledged by: ${comment.acknowledgedby._text} ${comment.acknowledgedon._text}`
                  : ""
              }
            >
              <span className="h6">
                {comment.createdby._text ===
                  JSON.parse(localStorage.getItem("safyInfo")).user
                    .displayName && (
                  <>
                    <i
                      title="Delete Comment"
                      className="far fa-trash-alt text-white me-2"
                      onClick={() => {
                        confirmAlert({
                          title: "Delete Comment",
                          message:
                            "Are you sure you want to delete this comment?",
                          buttons: [
                            {
                              label: "Yes",
                              onClick: () => {
                                handleDeleteComment(comment);
                              },
                            },
                            {
                              label: "No",
                              onClick: () => {
                                /* do nothing */
                              },
                            },
                          ],
                        });
                      }}
                    ></i>
                    {!comment.acknowledgedby && (
                      <i
                        title="Edit Comment"
                        className="fas fa-pen text-white me-2"
                        onClick={(e) => {
                          toggleCommentToEdit(comment);
                        }}
                      ></i>
                    )}
                  </>
                )}
                {comment.acknowledgedby && (
                  <i
                    className="far fa-check-square text-white me-2"
                    onClick={() => {
                      if (
                        comment.acknowledgedby._text ===
                        JSON.parse(localStorage.getItem("safyInfo")).user
                          .displayName
                      )
                        handleAcknowledgeComment(comment, false);
                    }}
                  ></i>
                )}
                {!comment.acknowledgedby && (
                  <i
                    className="far fa-square text-white me-2"
                    onClick={() => {
                      handleAcknowledgeComment(comment, true);
                    }}
                    title="Acknowledge Comment"
                  ></i>
                )}
                <br />
                {`${comment.createdby._text} - ${comment.datetime._text}`}
              </span>
              <br />
              Regarding:{" "}
              {comment.field._text !== "EntireForm" &&
              comment.field._text !== "Entire Form----" ? (
                <button
                  className="btn btn-link"
                  onClick={() => {
                    setTimeout(() => {
                      handleCommentNavigation(comment.field._text);
                    }, 100);
                  }}
                >
                  {comment.field._text}
                </button>
              ) : (
                <>{comment.field._text}</>
              )}
              <br />
              {comment.comment._text}
            </div>
          )}
        </div>
      ));
  };

  function scrollTo(offset, callback) {
    const fixedOffset = offset.toFixed();
    const onScroll = function () {
      if (window.pageYOffset.toFixed() === fixedOffset) {
        window.removeEventListener("scroll", onScroll);
        callback();
      }
    };

    window.addEventListener("scroll", onScroll);
    onScroll();
    window.scrollTo({
      top: offset,
      behavior: "smooth",
    });
  }

  //SIGNATURE FUNCTIONALITY
  const toggleSignatureModal = (e) => {
    e.preventDefault();
    if (!isSaveInProgress) setShowSignatureModal(true);
  };

  const saveSignature = async (signatureData) => {
    console.log("Signature Data:", signatureData);

    let sigArray = [];
    if (Array.isArray(signatureData)) {
      sigArray = signatureData;
    } else {
      sigArray.push(signatureData);
    }
    setSignatures(sigArray);

    const formsyDetailsObj = { ...formsyDetails };

    if (formsyDetailsObj.FinalSigned !== "1" && checkFinalSigned(sigArray)) {
      console.log("Final Signed");
      formsyDetailsObj.FinalSigned = "1";

      if (
        formsyDetailsObj.FinalSignedDate === "" ||
        formsyDetailsObj.FinalSignedDate === "1900-01-01T00:00:00"
      ) {
        formsyDetailsObj.FinalSignedDate = new Date();
      }

      setFormsyDetails(formsyDetailsObj);
      setComments(undefined);
    }

    handleSubmit(
      null,
      formsyDetailsObj,
      formsyDetailsObj.FinalSigned === "1" ? undefined : null,
      sigArray
    );
  };

  const checkFinalSigned = (sigArray) => {
    const requiredSignatureTypes = helperConfiguration.finalSignType.split(",");

    const capturedSignatureTypes = sigArray.map((sig) => sig.signatureType);
    console.log("requiredSignatureTypes: ", requiredSignatureTypes);
    console.log("capturedSignatureTypes: ", capturedSignatureTypes);

    const missingSigTypes = requiredSignatureTypes.filter(
      (type) => !capturedSignatureTypes.includes(type)
    );

    return missingSigTypes.length === 0;
  };

  //COMPARE
  const compare = (field) => {
    if (doCompare)
      return compareForms(field, data, allFormSubmissions, formsyDetails);
  };

  const toggleCompare = () => {
    setDoCompare(!doCompare);
  };

  //CUSTOM
  const [newAmendment, setNewAmendment] = useState("");

  const resolutionAmendmentsMemo = useMemo(() => {
    if (
      !getFieldValue(data.ResolutionAmendments) ||
      !Array.isArray(JSON.parse(getFieldValue(data.ResolutionAmendments)))
    ) {
      return [];
    }

    return JSON.parse(getFieldValue(data.ResolutionAmendments));
  }, [data]);

  const addAmendment = () => {
    let amendments = [];

    if (getFieldValue(data.ResolutionAmendments)) {
      const resolutionAmendments = JSON.parse(
        getFieldValue(data.ResolutionAmendments)
      );
      amendments = Array.isArray(resolutionAmendments)
        ? resolutionAmendments
        : [];
    }

    const amendment = {
      _id: cryptoRandomString(10),
      CreatedBy: safyInfo.user.displayName,
      CreatedOn: new Date(),
      Amendment: newAmendment,
    };
    amendments.push(amendment);

    const obj = { ...data };
    obj.ResolutionAmendments["@value"] = JSON.stringify(amendments);
    setData(obj);

    handleSubmit(obj);
    setNewAmendment("");
  };

  const getSelectedProgramOption = () => {
    let program;
    if (clientData.programs !== undefined && data.SelectedProgram !== undefined)
      program = clientData.programs.filter(
        (p) => p.ccx_client_programid === data.SelectedProgram["@value"]
      )[0];

    if (program !== undefined) return program;

    return { ccx_company: { "@name": "" } };
  };

  ////UI
  return (
    <>
      <div className="mb-2">
        <BackIcon />
      </div>
      <div className={!submissionid ? "watermarked" : ""} ref={pdfRef}>
        <ClientFormHeader
          formTitle={formsyConfig.FormDescription}
          clientData={clientData.client}
          formsyDetails={formsyDetails}
        ></ClientFormHeader>
        <FormButtonBar
          config={
            formsyConfig.HelperConfiguration
              ? JSON.parse(formsyConfig.HelperConfiguration[0]).buttonBarConfig
              : {
                  sign: {
                    display: false,
                    initialSignatureType: "supervisor",
                  },
                  clone: {
                    display: false,
                  },
                  delete: {
                    display: false,
                  },
                  share: {
                    display: false,
                  },
                  comment: {
                    display: false,
                  },
                }
          }
          onSave={handleSubmit}
          onClone={handleOnClone}
          onDelete={handleDelete}
          onSignClicked={toggleSignatureModal}
          onComment={toggleCommentModal}
          onCompare={toggleCompare}
          onShare={toggleShareModal}
          signed={signatures.length > 0}
          finalSigned={formsyDetails.FinalSigned === "1"}
          validate={validate}
          pdfRef={pdfRef}
          filename={`${formsyConfig.FormDescription}`}
          newestVersion={
            formsyConfig.VersionIntId === formsyConfig.LatestVersionIntId
          }
        />
        <div className="py-2 border-bottom border-dark">
          <b>Selected Client Program:</b>{" "}
          {getSelectedProgramName(data, clientData)}
        </div>
        {renderComments()}
        {parsedComments.filter(
          (comment) =>
            comment.field._text === "EntireForm" ||
            comment.field._text === "Entire Form----"
        ).length > 0 && (
          <i
            className="fas fa-comment-alt text-warning float-end mb-2 ms-2"
            onClick={() => setShowFormComment(!showFormComment)}
          ></i>
        )}
        <div className="mt-4">
          {data && Object.keys(data).length > 0 && (
            <>
              <div className="clearfix d-print-none">
                <div className="float-end">
                  <button
                    className="btn btn-link"
                    title="Copy and Open Encounter"
                    onClick={() => {
                      const encounterEnvironment =
                        process.env.REACT_APP_ENVIRONMENT === "Production"
                          ? "ccplive-cp"
                          : "d365base-cp";
                      handleCopyUrl(location.pathname).then(() => {
                        window.open(
                          `https://${encounterEnvironment}.safy.org/dist/EncounterPortal/index.html?contactid=%7B${clientid}%7D`
                        );
                      });
                    }}
                  >
                    Copy form link and launch to Encounter
                  </button>
                </div>
              </div>
              <FormInput
                name="SafetyPlanDate"
                label="Safety Plan Date"
                type="date"
                error={errors.SafetyPlanDate}
                value={data.SafetyPlanDate["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <h5>
                <b>What behavior(s) is this Safety Plan Targeting?</b>
              </h5>
              <div className="pt-2 border d-flex flex-wrap">
                <FormCheckbox
                  name="SuicidalIdeation"
                  label="Suicidal Ideation"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.SuicidalIdeation["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="HomicidalIdeations"
                  label="Homicidal Ideation"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.HomicidalIdeations["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="SelfHarm"
                  label="Self-Harm"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.SelfHarm["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="AWOL"
                  label="AWOL"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.AWOL["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="PropertyDestruction"
                  label="Property Destruction"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.PropertyDestruction["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="PhysicalAggression"
                  label="Physical Aggression"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.PhysicalAggression["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="VerbalAggression"
                  label="Verbal Aggression"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.VerbalAggression["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="SubstanceUse"
                  label="Substance Use"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.SubstanceUse["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="UnsafeSleepBehaviors"
                  label="Unsafe Sleep Behaviors"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.UnsafeSleepBehaviors["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="UnsafeActsTowardsAnimals"
                  label="Unsafe Acts Towards Animals"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.UnsafeActsTowardsAnimals["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="UnsafeSexualBehaviors"
                  label="Unsafe Sexual Behaviors"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.UnsafeSexualBehaviors["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="HumanTraffickingVictim"
                  label="Victim of Human Trafficking"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.HumanTraffickingVictim["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="OtherBehaviors"
                  label="Other:"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.OtherBehaviors["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.OtherBehaviors["@value"] === "true" ? (
                  <FormInput
                    name="OtherBehaviorsDetails"
                    error={errors.OtherBehaviorsDetails}
                    value={data.OtherBehaviorsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("OtherBehaviorsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </div>
              <br />
              {data.SubstanceUse["@value"] === "true" ? (
                <>
                  <FormTextArea
                    name="TriggerDetails"
                    label="Describe triggers specific to substance use here:"
                    error={errors.TriggerDetails}
                    value={data.TriggerDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("TriggerDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <br />
                </>
              ) : null}
              {data.UnsafeSleepBehaviors["@value"] === "true" ||
              data.UnsafeActsTowardsAnimals["@value"] === "true" ||
              data.UnsafeSexualBehaviors["@value"] === "true" ? (
                <>
                  <FormTextArea
                    name="UnsafeActsDetails"
                    label="Describe unsafe behaviors here:"
                    error={errors.UnsafeActsDetails}
                    value={data.UnsafeActsDetails["@value"]}
                    identical={compare("UnsafeActsDetails")}
                    onChange={handleChange}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                  <br />
                </>
              ) : null}
              {data.SuicidalIdeation["@value"] === "true" ? (
                <>
                  <div className="pt-2 border p-2">
                    <h5>
                      <b>Additional Questions for Suicidal Ideation</b>
                    </h5>
                    <div className="p-2 border">
                      <FormTextArea
                        name="ReasonsForLiving"
                        label="My Reasons for Living"
                        error={errors.ReasonsForLiving}
                        value={data.ReasonsForLiving["@value"]}
                        onChange={handleChange}
                        identical={compare("ReasonsForLiving")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                        placeholder="future plans, things to look forward to, etc..."
                      />
                      <table
                        className="table border w-75"
                        style={{ tableLayout: "fixed" }}
                      >
                        <thead className="table-secondary">
                          <tr>
                            <th colspan="10" className="text-center">
                              Suicidality Scale
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr>
                            <td colspan="5">No Plan or Intent</td>
                            <td colspan="5">
                              <div className="float-end">Plan and Intent</div>
                            </td>
                          </tr>
                          <tr>
                            <td>1</td>
                            <td>2</td>
                            <td>3</td>
                            <td>4</td>
                            <td>5</td>
                            <td>6</td>
                            <td>7</td>
                            <td>8</td>
                            <td>9</td>
                            <td>10</td>
                          </tr>
                        </tbody>
                      </table>
                      <FormInput
                        name="FeelingToday"
                        label="Where would you scale yourself today?"
                        error={errors.FeelingToday}
                        value={data.FeelingToday["@value"]}
                        onChange={handleChange}
                        identical={compare("FeelingToday")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="FeelingAtWorst"
                        label="What was your number at your worst?"
                        error={errors.FeelingAtWorst}
                        value={data.FeelingAtWorst["@value"]}
                        onChange={handleChange}
                        identical={compare("FeelingAtWorst")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="FeelingWorryPoint"
                        label='What is the "worry point" (i.e., when there would be cause for concern)?'
                        error={errors.FeelingWorryPoint}
                        value={data.FeelingWorryPoint["@value"]}
                        onChange={handleChange}
                        identical={compare("FeelingWorryPoint")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="CheckInFrequency"
                        label="How often will your caregiver ask where you are on the scale?"
                        error={errors.CheckInFrequency}
                        value={data.CheckInFrequency["@value"]}
                        onChange={handleChange}
                        identical={compare("CheckInFrequency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                      <FormInput
                        name="ScalingQuestion"
                        label="How will they ask you the scaling question?"
                        error={errors.ScalingQuestion}
                        value={data.ScalingQuestion["@value"]}
                        onChange={handleChange}
                        identical={compare("ScalingQuestion")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </div>
                  </div>
                  <br />
                </>
              ) : null}
              <h5>
                <b>My Warning Signs</b> (thoughts, feelings, body sensations,
                situations, behaviors):
              </h5>
              <div className="pt-2 border d-flex flex-wrap">
                <FormCheckbox
                  name="Sweating"
                  label="Sweating"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Sweating["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="BreathingHard"
                  label="Breathing hard"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.BreathingHard["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RacingHeart"
                  label="Racing heart"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RacingHeart["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="ClenchingTeeth"
                  label="Clenching teeth"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.ClenchingTeeth["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="ClenchingFists"
                  label="Clenching fists"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.ClenchingFists["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RedFaced"
                  label="Red faced"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RedFaced["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="LoudVoice"
                  label="Loud voice"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.LoudVoice["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Rocking"
                  label="Rocking"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Rocking["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Fidgety"
                  label="Fidgety"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Fidgety["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Crying"
                  label="Crying"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Crying["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="SleepingAlot"
                  label="Sleeping a lot"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.SleepingAlot["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="DifficultySleeping"
                  label="Difficulty sleeping"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.DifficultySleeping["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Pacing"
                  label="Pacing"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Pacing["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Nauseous"
                  label="Nauseous / stomachache"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Nauseous["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Alone"
                  label="Wanting to be alone"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Alone["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RacingThoughts"
                  label="Racing thoughts"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RacingThoughts["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="FallAsleep"
                  label="Wanting to fall asleep"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.FallAsleep["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="OtherSign"
                  label="Other:"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.OtherSign["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.OtherSign["@value"] === "true" ? (
                  <FormInput
                    name="OtherSignDetails"
                    error={errors.OtherSignDetails}
                    value={data.OtherSignDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("OtherSignDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </div>
              <br />
              <h5>
                <b>My Coping Strategies</b> (skills, activities, distractions):
              </h5>
              <div className="p-2 border d-flex flex-wrap">
                <FormCheckbox
                  name="Yelling"
                  label="Yelling"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Yelling["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Reading"
                  label="Reading"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Reading["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Exercise"
                  label="Exercise / sports"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Exercise["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Writing"
                  label="Writing"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Writing["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Drawing"
                  label="Drawing / coloring"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Drawing["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Watching"
                  label="Watching TV / movie"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Watching["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Support"
                  label="Support from friends"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Support["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Playing"
                  label="Playing video games"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Playing["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Shower"
                  label="Taking a shower"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Shower["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Music"
                  label="Listening to music"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Music["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Walking"
                  label="Walking"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Walking["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Blanket"
                  label="Weighted blankets / vests"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Blanket["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="Animals"
                  label="Being with animals"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.Animals["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="OtherStrategy"
                  label="Other:"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.OtherStrategy["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.OtherStrategy["@value"] === "true" ? (
                  <FormInput
                    name="OtherStrategyDetails"
                    error={errors.OtherStrategyDetails}
                    value={data.OtherStrategyDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("OtherStrategyDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </div>
              <br />
              <h5>
                <b>My Supports</b> (people, places, pets):
              </h5>
              <FormTextArea
                name="MySupports"
                error={errors.MySupports}
                value={data.MySupports["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <h5>
                <b>My Plan to Stay Safe:</b>
              </h5>
              <FormTextArea
                name="SafetyPlan"
                error={errors.SafetyPlan}
                value={data.SafetyPlan["@value"]}
                onChange={handleChange}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <h5>
                <b>Steps to make the Environment Safe:</b>
              </h5>
              <div className="pt-2 border d-flex flex-wrap">
                <FormCheckbox
                  name="RestrictWeapons"
                  label="Restrict Access to Weapons/Firearms"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RestrictWeapons["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RestrictMedications"
                  label="Restrict Access to Medications"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RestrictMedications["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RestrictSharps"
                  label="Restrict Access to Sharps"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RestrictSharps["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RestrictPoisons"
                  label="Restrict Access to Poisonous Substances"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RestrictPoisons["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RestrictAlcohol"
                  label="Restrict access to Alcohol"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RestrictAlcohol["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="RestrictLighters"
                  label="Restrict Access to lighters/matches"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.RestrictLighters["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="AlternativeTransportation"
                  label="Plan for alternative transportation"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.AlternativeTransportation["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                <FormCheckbox
                  name="OtherSteps"
                  label="Other:"
                  divClassName="ms-3"
                  divStyle={{ width: "200px" }}
                  checked={data.OtherSteps["@value"] === "true"}
                  onChange={handleChange}
                  commentsHandler={handleComments}
                  disabled={formDisabled}
                />
                {data.OtherSteps["@value"] === "true" ? (
                  <FormInput
                    name="OtherStepsDetails"
                    error={errors.OtherStepsDetails}
                    value={data.OtherStepsDetails["@value"]}
                    onChange={handleChange}
                    identical={compare("OtherStepsDetails")}
                    commentsHandler={handleComments}
                    disabled={formDisabled}
                  />
                ) : null}
              </div>
              <FormTextArea
                name="AdditionalSteps"
                label="Additional description of how to achieve a safe environment, if applicable:"
                error={errors.AdditionalSteps}
                value={data.AdditionalSteps["@value"]}
                onChange={handleChange}
                identical={compare("AdditionalSteps")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <FormTextArea
                name="AdditionalComments"
                label="Additional comments:"
                error={errors.AdditionalComments}
                value={data.AdditionalComments["@value"]}
                onChange={handleChange}
                identical={compare("AdditionalComments")}
                commentsHandler={handleComments}
                disabled={formDisabled}
              />
              <br />
              <h5>
                <b>
                  Emergency Information{" "}
                  <i>
                    **Always call 911 first for a medical or safety emergency
                  </i>
                </b>
              </h5>
              <table className="table table-bordered">
                <tbody>
                  <tr>
                    <td>
                      <FormInput
                        name="Hotline"
                        label="Hotline:"
                        error={errors.Hotline}
                        value={data.Hotline["@value"]}
                        onChange={handleChange}
                        identical={compare("Hotline")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <PhoneInput
                        name="Hospital"
                        label="Hospital/ED:"
                        error={errors.Hospital}
                        value={data.Hospital["@value"]}
                        onChange={handleChange}
                        identical={compare("Hospital")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <PhoneInput
                        name="CrisisCenter"
                        label="Crisis Center:"
                        error={errors.CrisisCenter}
                        value={data.CrisisCenter["@value"]}
                        onChange={handleChange}
                        identical={compare("CrisisCenter")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </td>
                    <td>
                      <FormInput
                        name="OtherEmergency"
                        label="Other:"
                        error={errors.OtherEmergency}
                        value={data.OtherEmergency["@value"]}
                        onChange={handleChange}
                        identical={compare("OtherEmergency")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <PhoneInput
                        name="SAFYCrisisLine"
                        label="SAFY Crisis Line:"
                        error={errors.SAFYCrisisLine}
                        value={data.SAFYCrisisLine["@value"]}
                        onChange={handleChange}
                        identical={compare("SAFYCrisisLine")}
                        commentsHandler={handleComments}
                        disabled={formDisabled}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
              {formsyDetails.FinalSigned === "1" ? (
                <>
                  <div className="border rounded my-4 p-2 bg-light">
                    <div className="h6">{data.ResolutionHeader["@label"]}</div>
                    <div className="my-4">
                      {resolutionAmendmentsMemo.map((item) => (
                        <div className="mt-2 mb-4 mx-4" key={item._id}>
                          <div className="w-100">
                            <div
                              className={`mx-4 h6 ${
                                safyInfo.user.displayName === item.CreatedBy
                                  ? " text-end"
                                  : "text-start"
                              }`}
                            >
                              {`${item.CreatedBy} - ${new Date(
                                item.CreatedOn
                              ).toLocaleString()}`}
                            </div>
                          </div>
                          <div className="border rounded bg-white p-2">{`${item.Amendment}`}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mb-4">
                      <FormInput
                        name="NewAmendment"
                        label="New Resolution/Amendment:"
                        onChange={({ currentTarget: input }) => {
                          setNewAmendment(input.value);
                        }}
                        value={newAmendment}
                      />
                    </div>
                    <div className="row">
                      <div className="col">
                        <div
                          className="btn btn-success float-end me-4 mb-2"
                          onClick={() => addAmendment()}
                        >
                          Add
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : null}
            </>
          )}
          <div>
            <div
              className="btn btn-primary m-2 d-print-none"
              onClick={() => {
                handleSubmit(null, null, null, null);
              }}
            >
              Save
            </div>
            <Signature
              canvasWidth="450"
              canvasHeight="200"
              modalTitle="Report Signatures"
              careTeams={careTeams}
              signatures={signatures}
              onSign={saveSignature}
              clientData={clientData.client}
              showModal={showSignatureModal}
              onHide={() => {
                setShowSignatureModal(false);
              }}
              finalSignType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).finalSignType
              }
              initialSignatureType={
                formsyConfig.HelperConfiguration &&
                JSON.parse(formsyConfig.HelperConfiguration[0]).signatureType
              }
            ></Signature>
            <Comment
              showModal={showComment}
              commentToEdit={commentToEdit}
              onHide={() => {
                setShowComment(false);
                setCommentToEdit(undefined);
              }}
              onSave={onCommentSave}
              fields={commentFields()}
            ></Comment>
            {showShare ? (
              <ShareModal
                showShare={showShare}
                onHide={() => setShowShare(false)}
                onSend={handleShareSend}
                submissionId={submissionid}
              ></ShareModal>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}

export default SAFYAdultSafetyPlanv2;
